// ============================================================================
// broilerHatchery.ts — how each hatchery's birds actually did
// ----------------------------------------------------------------------------
// The redesigned Poultry Dashboard replaces the breed comparison with a
// hatchery one: hatchery · batches · average mortality, best first, with a
// "Best" chip on the lowest and a "High" chip above the owner's threshold
// (handoff 2026-10, Poultry Dashboard notes).
//
// Nothing new is stored. A batch already names its hatchery, and
// calcBroilerStatsFromDailys already derives mortality from the daily reports,
// so this only groups and averages what is there (R34: presentation phases
// never add a table).
//
// AVERAGE OF RATES, NOT A POOLED RATE. Each batch contributes its own mortality
// percentage once, so a 2,000-bird batch does not drown out a 300-bird one. The
// owner reads this table as "how do this hatchery's batches tend to do", which
// is a question about batches, not about birds.
// ============================================================================
import {calcBroilerStatsFromDailys} from './broiler.js';
import type {BroilerBatch, BroilerDailyRow} from './broiler.js';

/** Above this average, a hatchery is flagged (Poultry Dashboard notes). */
export const HATCHERY_HIGH_MORTALITY_PCT = 4;

export type HatcheryRow = {
  hatchery: string;
  batches: number;
  /** Mean of each batch's own mortality percentage, or null when none can be computed. */
  avgMortPct: number | null;
  /** Birds started across the counted batches, for the row's title text. */
  startedBirds: number;
  /** The lowest average in the table. */
  best: boolean;
  /** Above HATCHERY_HIGH_MORTALITY_PCT. */
  high: boolean;
};

/**
 * One row per hatchery across the batches given, sorted best (lowest average
 * mortality) first. A batch with no hatchery recorded, or whose mortality
 * cannot be computed, is left out rather than bucketed as "Unknown" — an
 * invented bucket would read as a hatchery that does not exist.
 */
export function hatcheryComparison(
  batches: readonly BroilerBatch[] | null | undefined,
  broilerDailys: readonly BroilerDailyRow[] | null | undefined,
): HatcheryRow[] {
  const buckets = new Map<string, {pcts: number[]; started: number}>();
  for (const batch of batches ?? []) {
    const hatchery = String(batch?.hatchery ?? '').trim();
    if (!hatchery) continue;
    const stats = calcBroilerStatsFromDailys(batch, broilerDailys);
    // A batch with no started birds has no rate to contribute; counting it as
    // 0% would make a hatchery look perfect for having no data.
    if (!(stats.startedBirds > 0)) continue;
    const bucket = buckets.get(hatchery) ?? {pcts: [], started: 0};
    bucket.pcts.push(stats.mortPct);
    bucket.started += stats.startedBirds;
    buckets.set(hatchery, bucket);
  }

  const rows: HatcheryRow[] = [...buckets.entries()].map(([hatchery, {pcts, started}]) => ({
    hatchery,
    batches: pcts.length,
    avgMortPct: pcts.length ? pcts.reduce((sum, p) => sum + p, 0) / pcts.length : null,
    startedBirds: started,
    best: false,
    high: false,
  }));

  rows.sort((a, b) => (a.avgMortPct ?? Infinity) - (b.avgMortPct ?? Infinity) || a.hatchery.localeCompare(b.hatchery));

  const lowest = rows.find((r) => r.avgMortPct != null)?.avgMortPct;
  for (const row of rows) {
    row.best = row.avgMortPct != null && lowest != null && row.avgMortPct === lowest && rows.length > 1;
    row.high = row.avgMortPct != null && row.avgMortPct > HATCHERY_HIGH_MORTALITY_PCT;
  }
  return rows;
}
